import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ExternalLink, ShoppingCart, TrendingUp, TrendingDown, RefreshCw, AlertCircle } from 'lucide-react';
import { getSkinPricing } from '../services/dmarket';

const DMarketPricing = ({ weaponName, skinName }) => {
  const [pricingData, setPricingData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadPricingData();
  }, [weaponName, skinName]);

  const loadPricingData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const data = await getSkinPricing(weaponName, skinName);
      setPricingData(data);
      
      if (!data.found) {
        setError(data.message || 'Item not found on DMarket');
      }
    } catch (err) {
      setError('Failed to load pricing data');
      console.error('DMarket pricing error:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (price) => {
    if (!price) return 'N/A';
    return `$${(price / 100).toFixed(2)}`;
  };

  const getRarityColor = (rarity) => {
    const rarityColors = {
      'Consumer Grade': 'text-gray-400',
      'Industrial Grade': 'text-blue-400',
      'Mil-Spec Grade': 'text-purple-400',
      'Restricted': 'text-pink-400',
      'Classified': 'text-red-400',
      'Covert': 'text-yellow-400',
      'Extraordinary': 'text-orange-400'
    };
    return rarityColors[rarity] || 'text-gray-400';
  };

  const cleanSkinName = (title) => {
    if (!title) return '';
    // Remove only wear conditions from the main title, keep StatTrak and other important info
    return title
      .replace(/\s*\((Factory New|Minimal Wear|Field-Tested|Well-Worn|Battle-Scarred)\)$/i, '')
      .replace(/\s*\((FN|MW|FT|WW|BS)\)$/i, '')
      .trim();
  };

  if (loading) {
    return (
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="card p-6"
      >
        <div className="flex items-center space-x-3 mb-4">
          <ShoppingCart className="h-6 w-6 text-green-400" />
          <h3 className="text-xl font-semibold text-white">Buy This Skin</h3>
        </div>
        
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-400"></div>
          <span className="ml-3 text-gray-400">Loading marketplace data...</span>
        </div>
      </motion.div>
    );
  }

  if (error || !pricingData?.found) {
    return (
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="card p-6"
      >
        <div className="flex items-center space-x-3 mb-4">
          <ShoppingCart className="h-6 w-6 text-green-400" />
          <h3 className="text-xl font-semibold text-white">Buy This Skin</h3>
        </div>
        
        <div className="flex items-center space-x-3 py-6">
          <AlertCircle className="h-8 w-8 text-yellow-400 flex-shrink-0" />
          <div>
            <p className="text-gray-300 font-medium">Not available on marketplace</p>
            <p className="text-gray-500 text-sm mt-1">{error || 'This skin is not currently listed on DMarket'}</p>
          </div>
        </div>
        
        <div className="mt-4">
          <a
            href="https://dmarket.com?ref=krTE9HvlDC"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center space-x-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors"
          >
            <ExternalLink className="h-4 w-4" />
            <span>Browse DMarket</span>
          </a>
        </div>
      </motion.div>
    );
  }

  const { item, offers, stats, buyLink } = pricingData;

  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="card p-6"
    >
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <ShoppingCart className="h-6 w-6 text-green-400" />
          <h3 className="text-xl font-semibold text-white">Buy This Skin</h3>
        </div>
      </div>

      {/* Item Info */}
      <div className="flex items-center space-x-4 mb-6 p-4 bg-gray-700 rounded-lg">
        {item.image && (
          <img 
            src={item.image} 
            alt={item.title}
            className="w-16 h-16 object-cover rounded-lg bg-gray-800"
          />
        )}
        <div className="flex-1">
          <h4 className="font-medium text-white">{cleanSkinName(item.title)}</h4>
          <p className="text-sm text-gray-400 mt-1">Available on DMarket</p>
        </div>
      </div>

      {/* Price Stats */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="bg-gray-700 rounded-lg p-4 text-center">
          <div className="flex items-center justify-center mb-2">
            <TrendingDown className="h-4 w-4 text-green-400" />
          </div>
          <div className="text-lg font-bold text-white">{formatPrice(stats.minPrice)}</div>
          <div className="text-sm text-gray-400">Lowest Price</div>
        </div>
        
        <div className="bg-gray-700 rounded-lg p-4 text-center">
          <div className="flex items-center justify-center mb-2">
            <TrendingUp className="h-4 w-4 text-red-400" />
          </div>
          <div className="text-lg font-bold text-white">{formatPrice(stats.maxPrice)}</div>
          <div className="text-sm text-gray-400">Highest Price</div>
        </div>
        
        <div className="bg-gray-700 rounded-lg p-4 text-center">
          <div className="flex items-center justify-center mb-2">
            <ShoppingCart className="h-4 w-4 text-blue-400" />
          </div>
          <div className="text-lg font-bold text-white">{stats.totalOffers}</div>
          <div className="text-sm text-gray-400">Available</div>
        </div>
      </div>

      {/* Best Offers */}
      {offers.length > 0 && (
        <div className="mb-6">
          <h4 className="text-lg font-medium text-white mb-3">Best Offers</h4>
          <div className="space-y-2 max-h-48 overflow-y-auto">
            {offers.slice(0, 5).map((offer, index) => (
              <a
                key={offer.offerId || index}
                href={offer.individualBuyLink || buyLink}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-between p-3 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors cursor-pointer"
              >
                <div className="flex items-center space-x-3">
                  <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                  <div>
                    <div className="text-white font-medium">{formatPrice(offer.price?.USD)}</div>
                    <div className="flex items-center space-x-2 mt-1">
                      {offer.extra?.exterior && (
                        <span className="text-xs text-gray-400 bg-gray-800 px-2 py-1 rounded">
                          {offer.extra.exterior}
                        </span>
                      )}
                      {offer.title && offer.title.toLowerCase().includes('stattrak') && (
                        <span className="text-xs text-orange-400 bg-gray-800 px-2 py-1 rounded">
                          StatTrak™
                        </span>
                      )}
                      {offer.extra?.floatValue && (
                        <span className="text-xs text-purple-400 bg-gray-800 px-2 py-1 rounded">
                          Float: {parseFloat(offer.extra.floatValue).toFixed(4)}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="text-sm text-gray-400">
                    {offer.extra?.tradeLockDuration ? `Trade Lock: ${offer.extra.tradeLockDuration}d` : 'Tradable'}
                  </div>
                  <ExternalLink className="h-4 w-4 text-gray-500" />
                </div>
              </a>
            ))}
          </div>
        </div>
      )}

      {/* Buy Button */}
      <div className="flex space-x-3">
        <a
          href={buyLink}
          target="_blank"
          rel="noopener noreferrer"
          className="flex-1 flex items-center justify-center space-x-2 px-6 py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors font-medium"
        >
          <ShoppingCart className="h-5 w-5" />
          <span>Buy on DMarket</span>
          <ExternalLink className="h-4 w-4" />
        </a>
        
        <a
          href="https://dmarket.com?ref=krTE9HvlDC"
          target="_blank"
          rel="noopener noreferrer"
          className="px-4 py-3 bg-gray-700 hover:bg-gray-600 text-gray-300 rounded-lg transition-colors"
        >
          <ExternalLink className="h-5 w-5" />
        </a>
      </div>

      {/* Disclaimer */}
      <div className="mt-4 p-3 bg-gray-800 rounded-lg">
        <p className="text-xs text-gray-500">
          Prices are from DMarket marketplace and may change frequently.
        </p>
      </div>
    </motion.div>
  );
};

export default DMarketPricing;
